import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { useFormik } from 'formik';
import { Icon } from "@iconify/react";

export default function EditUserNameModel({
    open,
    onClose,
    propFirstName,
    propLastName,
    onUserUpdate,
    propCountry,
    propCity,
    propImage
}) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');

    const [isLoading, setIsLoading] = useState(false);
    const [organizationImagePreview, setOrganizationImagePreview] = useState(propImage || null);
    const [selectedFileName, setSelectedFileName] = useState("No File Chosen");

    const modalRef = useRef(null);

    useEffect(() => {
        if (open) {
            editUser.setValues({
                firstName: propFirstName || '',
                lastName: propLastName || '',
                country: propCountry || '',
                city: propCity || '',
                logo: propImage || '',
            });
        }
    }, [open, propFirstName, propLastName, propCountry, propCity, propImage]);

    const handleOrganizationImageChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            editUser.setFieldValue("logo", file);
            setOrganizationImagePreview(URL.createObjectURL(file));
            setSelectedFileName(file.name);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const onSubmit = async (values) => {
        if (!bearerToken) return;

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append("firstName", values.firstName);
            formData.append("lastName", values.lastName);
            formData.append("country", values.country);
            formData.append("city", values.city);
            if (values.logo instanceof File) {
                formData.append("logo", values.logo);
            }

            const response = await axios.put(`${apiUrl}auth-service/auth/update`, formData, {
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                    "Content-Type": "multipart/form-data"
                }
            });

            if (response.data?.success) {
                onUserUpdate();
                toast.success(response.data.message || "User details updated successfully!");
            } else {
                throw new Error("Unable to update user details.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    const handleClose = () => {
        onClose();
    };

    const editUser = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            country: '',
            city: '',
            logo: '',
        },
        onSubmit,
    });

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
            <div className="absolute inset-0 bg-black opacity-30" onClick={onClose}></div>

            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative p-6 shadow-lg" ref={modalRef}>
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto text-center -mt-12">
                    <h2 className="text-xl font-semibold">Edit User</h2>
                </div>

                <form onSubmit={editUser.handleSubmit} className="space-y-6 mt-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                            <input
                                type="text"
                                name="firstName"
                                value={editUser.values.firstName}
                                onChange={editUser.handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                            <input
                                type="text"
                                name="lastName"
                                value={editUser.values.lastName}
                                onChange={editUser.handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700">City</label>
                            <input
                                type="text"
                                name="city"
                                value={editUser.values.city}
                                onChange={editUser.handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700">Country</label>
                            <input
                                type="text"
                                name="country"
                                value={editUser.values.country}
                                onChange={editUser.handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </div>

                    <div className="flex items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                            {organizationImagePreview ? (
                                <img src={organizationImagePreview} alt="profile" className="w-16 h-16 object-cover rounded-full" />
                            ) : (
                                <span className="text-gray-400 text-2xl">+</span>
                            )}
                        </div>
                        <div>
                            <p className="text-xs italic text-gray-600 mb-1">Upload an image (max 5MB)</p>
                            <div className="flex space-x-2">
                                <label className="px-3 py-1 bg-white border border-blue-500 text-blue-500 rounded cursor-pointer text-sm">
                                    Choose File
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={handleOrganizationImageChange}
                                    />
                                </label>
                                <span className="text-sm text-gray-500">{selectedFileName}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                            {isLoading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

EditUserNameModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    propFirstName: PropTypes.string.isRequired,
    propLastName: PropTypes.string.isRequired,
    propEmail: PropTypes.string.isRequired,
    propPhone: PropTypes.string.isRequired,
    propCity: PropTypes.string,
    propCountry: PropTypes.string,
    onUserUpdate: PropTypes.func.isRequired,
    propImage: PropTypes.string,
};
